<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ROUTES } from '@/router/routeNames'
import { useToast } from 'primevue/usetoast'
import api from '@/services/axios'
import PrimaryButton from '@/components/general/PrimaryButton.vue'
import { useFormValidation } from '@/composables/useFormValidation'
import { useField } from 'vee-validate'
import { businessSchema } from '@/formValidationschemas'
import QRCode from '@/components/general/QRCode.vue'
import { businessQRCodeUrl } from '@/constants'
const router = useRouter()
const route = useRoute()
const toast = useToast()

const business = ref(null)
const isLoading = ref(true)

// Initialize form validation
const { handleSubmit, errors } = useFormValidation(businessSchema)

// Create fields with validation
const { value: name, setValue: setName } = useField('name')
const { value: description, setValue: setDescription } = useField('description')
const { value: address, setValue: setAddress } = useField('address')
const { value: phone, setValue: setPhone } = useField('phone')
const { value: is_active, setValue: setIsActive } = useField('is_active')
const { value: packs, setValue: setPacks } = useField('packs')

const businessId = route.params.id

const fetchBusiness = async () => {
  try {
    isLoading.value = true
    const response = await api.get(`api/business/${businessId}`)
    business.value = response.data

    // Populate the form with business data
    setName(business.value.name || '')
    setDescription(business.value.description || '')
    setAddress(business.value.address || '')
    setPhone(business.value.phone || '')
    setIsActive(business.value.is_active)
    setPacks(business.value.packs)
  } catch (error) {
    console.error('Error fetching business:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load business details',
      life: 3000,
    })
    router.push({ name: ROUTES.DASHBOARD })
  } finally {
    isLoading.value = false
  }
}

const showQRCode = ref(false)

const submit = handleSubmit(async (values) => {
  try {
    isLoading.value = true
    await api.put(`api/business/${businessId}`, values)
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Business updated successfully',
      life: 3000,
    })
    router.push({ name: ROUTES.DASHBOARD })
  } catch (error) {
    console.error('Error updating business:', error)

    // Handle validation errors from the server
    if (error.response && error.response.data && error.response.data.errors) {
      // Map server errors to form fields if needed
      // const serverErrors = error.response.data.errors
      // Object.keys(serverErrors).forEach((key) => {
      //   // You could set these errors manually if needed
      // })
    }

    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || 'Failed to update business',
      life: 3000,
    })

    isLoading.value = false
  }
})

onMounted(() => {
  fetchBusiness()
})
</script>

<template>
  <div class="sm:py-12">
    <div class="sm:px-6 lg:px-8">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Edit Business Profile</h1>
      </div>

      <div class="overflow-hidden bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg">
        <!-- Loading State -->
        <div v-if="isLoading" class="flex justify-center items-center p-12">
          <div
            class="w-12 h-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"
          ></div>
        </div>

        <!-- Form -->
        <div v-else class="p-6 text-gray-900 dark:text-gray-100">
          <div class="flex justify-end">
            <button
              class="px-6 py-2 text-white bg-green-800 rounded-xl cursor-pointer"
              @click="showQRCode = !showQRCode"
            >
              {{ showQRCode ? 'Hide' : 'Show' }} QRCode
            </button>
          </div>
          <div class="col-span-3 mt-6" v-if="showQRCode">
            <p class="text-sm text-gray-500 dark:text-gray-400">Business QR Code</p>
            <div class="mt-2">
              <QRCode
                :value="businessQRCodeUrl(business)"
                :size="150"
                :show-download="true"
                :show-print="true"
                :download-name="`business-${business?.uuid}`"
                :business-name="business?.name"
                alt="Business QR Code"
              />
              <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Scan this code to share this business page
              </p>
            </div>
          </div>
          <form @submit.prevent="submit" class="mt-6 space-y-6">
            <div>
              <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Business Name</label
              >
              <input
                type="text"
                id="name"
                v-model="name"
                class="block p-3 mt-1 w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <span v-if="errors.name" class="mt-1 text-sm text-red-500">{{ errors.name }}</span>
            </div>

            <div>
              <label
                for="description"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Description</label
              >
              <textarea
                id="description"
                v-model="description"
                rows="3"
                class="block p-3 mt-1 w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              ></textarea>
              <span v-if="errors.description" class="mt-1 text-sm text-red-500">{{
                errors.description
              }}</span>
            </div>

            <div>
              <label
                for="address"
                class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Address</label
              >
              <input
                type="text"
                id="address"
                v-model="address"
                class="block p-3 mt-1 w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <span v-if="errors.address" class="mt-1 text-sm text-red-500">{{
                errors.address
              }}</span>
            </div>

            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Phone</label
              >
              <input
                type="text"
                id="phone"
                v-model="phone"
                class="block p-3 mt-1 w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <span v-if="errors.phone" class="mt-1 text-sm text-red-500">{{ errors.phone }}</span>
            </div>
            <div>
              <label for="packs" class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Packs</label
              >
              <small>Do you sell packs? please enter the price of a pack</small>
              <input
                type="number"
                id="packs"
                v-model="packs"
                class="block p-3 mt-1 w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <span v-if="errors.packs" class="mt-1 text-sm text-red-500">{{ errors.packs }}</span>
            </div>

            <!-- <div>
                            <label for="logo" class="block text-sm font-medium text-gray-700 dark:text-gray-300">Logo URL</label>
                            <input type="text" id="logo" v-model="logo" class="block p-3 mt-1 w-full rounded-md border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                            <span v-if="errors.logo" class="mt-1 text-sm text-red-500">{{ errors.logo }}</span>
                        </div> -->

            <div class="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                v-model="is_active"
                class="p-3 text-indigo-600 rounded border border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
              <label for="is_active" class="block ml-2 text-sm text-gray-700 dark:text-gray-300"
                >Active</label
              >
            </div>

            <div
              class="flex justify-end items-center pt-4 border-t border-gray-200 dark:border-gray-700"
            >
              <PrimaryButton type="submit" :disabled="isLoading" class="ml-3">
                Save Changes
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
